import { useState } from "react";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { HiExclamation } from "react-icons/hi";

import { StatusDot } from "../../../../components";
import { useIntegrations } from "../../../../contexts/IntegrationsContext";
import { useAuth, useToast } from "../../../../hooks";
import { NOTIFICATION_TYPES } from "../../../../utils";
import { DisconnectConfirmation } from "../DisconnectConfirmation";

const CONNECT_SLACK_ENDPOINT = "integration/team-integrations/connect-slack/";

export const SlackHeader = ({ isConnected, integration, credentials }) => {
  const { apiClient } = useAuth();
  const { fetchAllIntegrations, disconnectIntegration } = useIntegrations();
  const showToast = useToast();

  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [error, setError] = useState(null);

  const parseDomains = (raw) =>
    (raw || "")
      .split(",")
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);

  const handleConnect = async () => {
    if (!credentials?.token?.trim()) {
      setError("Bot token is required");
      return;
    }
    setConnectLoading(true);
    setError(null);
    try {
      await apiClient.post(CONNECT_SLACK_ENDPOINT, {
        token: credentials.token.trim(),
        team_domains: parseDomains(credentials.teamDomains),
      });
      await fetchAllIntegrations();
      showToast({
        content: "Slack connected successfully!",
        type: NOTIFICATION_TYPES.success,
      });
    } catch (err) {
      // Backend returns a safe Slack error code (e.g. invalid_auth); never the token.
      const code = err.response?.data?.error;
      setError(
        code === "invalid_auth"
          ? "Slack rejected that token. Copy the current Bot User OAuth Token and try again."
          : "Failed to connect Slack. Please try again.",
      );
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    setError(null);
    try {
      await disconnectIntegration(integration.id);
      setShowDisconnectModal(false);
    } catch (err) {
      setError("Failed to disconnect Slack. Please try again.");
    } finally {
      setDisconnectLoading(false);
    }
  };

  const isButtonLoading = connectLoading || disconnectLoading;

  return (
    <>
      {error && (
        <div className="mt-4 px-6">
          <div className="rounded-md bg-red-50 p-3">
            <div className="flex">
              <HiExclamation className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-row items-center justify-between px-6">
        <div className="flex flex-row items-center justify-center gap-2.5">
          <span className="mr-2 text-xl font-bold text-[#4A154B]">Slack</span>
          {isButtonLoading ? (
            <AiOutlineLoading className="h-6 w-6 animate-spin" />
          ) : (
            <StatusDot status={isConnected ? "green" : undefined} />
          )}
        </div>

        <div className="flex flex-row items-center justify-between gap-2">
          <Button
            color={isConnected ? "gray" : "blue"}
            className="w-36"
            disabled={isButtonLoading}
            isProcessing={isButtonLoading}
            onClick={
              isConnected ? () => setShowDisconnectModal(true) : handleConnect
            }
          >
            {isConnected ? "Disconnect" : "Connect"}
          </Button>
        </div>
      </div>

      <DisconnectConfirmation
        show={showDisconnectModal}
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectModal(false)}
        integrationName="Slack"
      />
    </>
  );
};
